import { z } from "zod";

/**
 * Sözleşme SORUMLULUĞU (CLOS-112 `[074]`).
 *
 * ### Üç tür, tek model
 *
 * Kart: *"ana sorumlu, katkı sağlayan kullanıcı ve departman ilişkileri desteklenen fakat
 * BAĞIMSIZ sorumluluk türleridir; her sözleşmede bütün türlerin bulunması gerekmez."* Üç
 * ayrı tablo yazsaydık her biri kendi uyarı, audit ve yetki kuralını taşır, biri gün gelip
 * diğerinden ayrışırdı.
 *
 * ### ⚠ Sorumluluk bir ERİŞİM KAYNAĞI DEĞİL
 *
 * Kart: *"sorumlu veya ekip üyesi olarak atanmak sözleşme kayıt erişimini OTOMATİK VERMEZ
 * ve mevcut permission sınırını aşmaz."* Bu dosyada ve onu tüketen serviste hiçbir yetki
 * tablosuna yazma yok. Tersi de geçerli: erişimi olmak kimseyi sorumlu yapmıyor.
 */

export const CONTRACT_RESPONSIBILITY_KINDS = [
  /** ANA sorumlu — sözleşme başına en fazla bir tane (tekillik veritabanında). */
  "owner",
  /** Katkı sağlayan kullanıcı — birden çok olabilir. */
  "contributor",
  /** İlgili departman — opsiyonel; departmansız organizasyon bloke edilmiyor. */
  "department",
] as const;
export type ContractResponsibilityKind = (typeof CONTRACT_RESPONSIBILITY_KINDS)[number];

/**
 * SORUMLULUK ALANLARI (CLOS-1126 · rapor §2.2.1 · Halit K1).
 *
 * Alan sorumlusu = `contributor + area`; yeni `kind` AÇILMADI — mevcut modele en az dokunan
 * çözüm. `owner` = "Sözleşme Sorumlusu" (alan yok), `contributor + area = legal` = "Hukuki
 * Sorumlu". Alan başına TEK birincil kişi (kısmi tekil indeks); aynı kişi birden çok alanı
 * taşıyabilir. `area` NULL olan contributor = genel "Katkı sağlayan" (eski satırlar aynen).
 *
 * ⚠ Alan bir ROUTING anahtarıdır, yetki değil: hukuki olay → legal, tarih/yenileme → renewal,
 * finans → yalnız yükümlülük formunda öneri. Departman üyeliği ya da destekleyen ekip hiçbir
 * zaman routing authority'ye dönüşmez (Halit routing kararı).
 */
export const CONTRACT_RESPONSIBILITY_AREAS = [
  "legal",
  "commercial",
  "finance",
  "operations",
  "renewal",
] as const;
export type ContractResponsibilityArea = (typeof CONTRACT_RESPONSIBILITY_AREAS)[number];

export const CONTRACT_RESPONSIBILITY_AREA_NAMES: Record<ContractResponsibilityArea, string> = {
  legal: "Hukuki Sorumlu",
  commercial: "Ticari Sorumlu",
  finance: "Finans Sorumlusu",
  operations: "Operasyon Sorumlusu",
  renewal: "Süre/Yenileme Sorumlusu",
};

export const CONTRACT_RESPONSIBILITY_KIND_NAMES: Record<ContractResponsibilityKind, string> = {
  owner: "Sözleşme Sorumlusu",
  contributor: "Katkı sağlayan",
  department: "İlgili departman",
};

/**
 * Kullanıcı sorumluluğu ÜYELİK üzerinden veriliyor, kullanıcı kimliği üzerinden değil.
 *
 * ⚠ Kart *"yalnız AKTİF VE UYGUN ÜYELİKLE seçilebilir"* diyor ve uygunluğun kaynağı üyelik
 * state machine'i (CLOS-77 `[038]`). Kullanıcı kimliğini saklasaydık, "bu kişi hâlâ bu
 * organizasyonda mı?" sorusu her okuma yolunda yeniden kurulan bir join'e kalırdı — ve
 * "üyelik sona erdiğinde uyarı" kriteri bağlanacak bir satır bulamazdı.
 */
export const assignMemberResponsibilitySchema = z.object({
  membershipId: z.uuid("Geçerli bir üye seçilmelidir"),
  /**
   * "Erişim ver ve ata" (CLOS-1138): hedefin kayıt erişimi yoksa ve aktör verebiliyorsa
   * minimum okuma erişimi + atama tek komutta. Yalnız kayıt-erişimi engelini çözer; rol
   * yükseltmez. Yoksa 409 `record_access_missing[_grantable]` döner.
   */
  grantAccess: z.boolean().optional(),
  /**
   * Destekleyen ekip (CLOS-1126 · Halit K2): kişi sorumluluğunun üstünde YALNIZ metadata;
   * erişim ya da yetki üretmez. Sunucu aynı organizasyon + arşivlenmemiş departman doğrular.
   */
  supportingDepartmentId: z.uuid("Geçerli bir departman seçilmelidir").optional(),
});
export type AssignMemberResponsibilityInput = z.infer<typeof assignMemberResponsibilitySchema>;

/**
 * Katkı sağlayan / alan sorumlusu: `area` verilirse alan sorumlusu — alan başına tek kişi,
 * mevcut sorumlu varsa atomik değişim (K1: 409 değil, upsert). `area` yoksa genel katkı sağlayan.
 */
export const assignContributorResponsibilitySchema = assignMemberResponsibilitySchema.extend({
  area: z.enum(CONTRACT_RESPONSIBILITY_AREAS).optional(),
});
export type AssignContributorResponsibilityInput = z.infer<
  typeof assignContributorResponsibilitySchema
>;

export const assignDepartmentResponsibilitySchema = z.object({
  departmentId: z.uuid("Geçerli bir departman seçilmelidir"),
});
export type AssignDepartmentResponsibilityInput = z.infer<
  typeof assignDepartmentResponsibilitySchema
>;

export type ContractResponsibility = {
  id: string;
  kind: ContractResponsibilityKind;
  /** Yalnız `contributor`'da dolu olabilir; NULL = genel katkı sağlayan (CLOS-1126). */
  area: ContractResponsibilityArea | null;
  /** Kullanıcı sorumluluklarında dolu; departman satırlarında `null`. */
  membershipId: string | null;
  userId: string | null;
  displayName: string | null;
  /** Üyeliğin O ANKİ durumu — uyarılar buradan doğuyor. */
  membershipStatus: string | null;
  departmentId: string | null;
  departmentName: string | null;
  departmentArchived: boolean;
  /** Destekleyen ekip (kişi satırlarında, opsiyonel) — bilgi amaçlı, erişim vermez. */
  supportingDepartmentId: string | null;
  supportingDepartmentName: string | null;
  assignedAt: string;
};

/**
 * UYARI KODLARI — kart: *"üyelik sona erdiğinde sorumluluk uyarısı oluşur"* ve *"departman
 * arşivlenirse ... yeniden atama uyarısı üretilir."*
 *
 * ⚠ Uyarı bir HATA DEĞİL, okunan durumun parçası. Fırlatsaydık, üyeliği sona ermiş bir
 * sorumlusu olan sözleşme hiç açılamaz ve yeniden atama da yapılamazdı — uyarının tek
 * amacı olan işlem, uyarı yüzünden imkânsız olurdu.
 */
export const RESPONSIBILITY_WARNING_CODES = [
  "membership_inactive",
  "department_archived",
  "no_record_access",
  /**
   * SORUMLUNUN ROLÜ UYGUNLUĞUNU KAYBETTİ (CLOS-1212, §12.3 `REASSIGN_REQUIRED`).
   *
   * ⚠ Atama anındaki kapı geleceği garanti etmiyor: rol sonradan uygunluktan çıkarılabilir.
   * Satırı o anda silseydik, tenant'ın bir rol ayarını değiştirmesi sözleşmelerin
   * sorumlusunu sessizce düşürürdü. Uyarı, kararı kullanıcıya bırakıyor.
   */
  "role_ineligible",
] as const;
export type ResponsibilityWarningCode = (typeof RESPONSIBILITY_WARNING_CODES)[number];

export type ResponsibilityWarning = {
  code: ResponsibilityWarningCode;
  responsibilityId: string;
  message: string;
};

/** Kullanıcıya dönük satır başlığı — tek tanım (K6: "Sözleşme Sorumlusu"). */
export function responsibilityTitle(r: {
  kind: ContractResponsibilityKind;
  area: ContractResponsibilityArea | null;
}): string {
  if (r.kind === "contributor" && r.area) return CONTRACT_RESPONSIBILITY_AREA_NAMES[r.area];
  return CONTRACT_RESPONSIBILITY_KIND_NAMES[r.kind];
}

export type ContractResponsibilities = {
  owner: ContractResponsibility | null;
  contributors: ContractResponsibility[];
  departments: ContractResponsibility[];
  warnings: ResponsibilityWarning[];
};

const UYARI_METNI: Record<ResponsibilityWarningCode, string> = {
  membership_inactive: "Sorumlunun üyeliği artık aktif değil; yeniden atama gerekiyor",
  role_ineligible:
    "Sorumlunun rolü Sözleşme Yöneticisi olarak atanmaya artık uygun değil; yeniden atama gerekiyor",
  department_archived: "İlgili departman arşivlendi; yeniden atama gerekiyor",
  /**
   * ⚠ Kart: *"erişim gerekiyorsa AÇIKÇA verilir."* Atama komutu erişimi sessizce üretmiyor;
   * bunun yerine eksikliği söylüyor ki yetkili kullanıcı CLOS-91 (`[077]`) yolundan açık
   * erişim verebilsin. Sessiz kalsaydık, sorumlu atanmış ama sözleşmeyi açamayan bir
   * kullanıcı bunu ancak bir 403 ekranında öğrenirdi.
   */
  no_record_access: "Bu kullanıcının sözleşmeye erişimi yok; erişim ayrıca verilmelidir",
};

export function responsibilityWarning(
  code: ResponsibilityWarningCode,
  responsibilityId: string,
): ResponsibilityWarning {
  return { code, responsibilityId, message: UYARI_METNI[code] };
}

/**
 * SÖZLEŞMEDE ÇALIŞAN EKİP (CLOS-1219 — Departmanlar Kanonu §6).
 *
 * ⚠ "İlgili departman" ile AYNI ŞEY DEĞİL. O bir etiket; bu gerçek bir operasyonel ilişki:
 * erişim üretir, workflow havuzunu besler, etki analizine girer (§6.5).
 */
export const CONTRACT_PARTICIPATION_TYPES = ["lead", "collaborating"] as const;
export type ContractParticipationType = (typeof CONTRACT_PARTICIPATION_TYPES)[number];

export const CONTRACT_MEMBER_SCOPES = ["all_active_members", "selected_members"] as const;
export type ContractMemberScope = (typeof CONTRACT_MEMBER_SCOPES)[number];

export const addContractDepartmentTeamSchema = z.object({
  departmentId: z.uuid("Geçerli bir departman seçilmelidir"),
  /**
   * ⚠ Zorunlu ve varsayılansız: "ana ekip mi, birlikte çalışan mı" sorusunun sessiz bir
   * cevabı olmamalı. Varsayılan koysaydık, ilk ekip eklenirken kullanıcının vermediği bir
   * karar sözleşmenin asıl sahibini belirlerdi.
   */
  participationType: z.enum(CONTRACT_PARTICIPATION_TYPES, {
    message: "Ekip türü seçilmelidir",
  }),
});
export type AddContractDepartmentTeamInput = z.infer<typeof addContractDepartmentTeamSchema>;

/**
 * ⚠ `idParamSchema` YETMEZ: bilinmeyen alanı reddeden şema, iki parametreli bir yolda
 * ikinci parametreyi 422'ye çevirir. Bu tuzak depoda daha önce iki kez yaşandı
 * (`contractRevision.ts`, `contractServiceAssignment.ts`) ve burada üçüncü kez ölçüldü:
 * silme ucu 422 döndü, erişim testleri kırmızı kaldı.
 */
export const contractDepartmentTeamParamsSchema = z.object({
  id: z.uuid(),
  participationId: z.uuid(),
});

export const contractDepartmentTeamSchema = z.object({
  id: z.uuid(),
  departmentId: z.uuid(),
  departmentName: z.string(),
  departmentArchived: z.boolean(),
  participationType: z.enum(CONTRACT_PARTICIPATION_TYPES),
  memberScope: z.enum(CONTRACT_MEMBER_SCOPES),
});
export type ContractDepartmentTeam = z.infer<typeof contractDepartmentTeamSchema>;

/**
 * LEGACY DEPARTMAN → ÇALIŞAN EKİP DÖNÜŞTÜRME (CLOS-1227, kilitli karar).
 *
 * ⚠ `participationType` ZORUNLU ve varsayılansız. Sistem "birlikte çalışan olsun" diye
 * karar veremez: dönüştürmenin bütün anlamı, iddiayı KULLANICININ koymasıdır.
 */
export const convertLegacyDepartmentSchema = z.object({
  participationType: z.enum(CONTRACT_PARTICIPATION_TYPES, {
    message: "Ekip türü seçilmelidir",
  }),
});
export type ConvertLegacyDepartmentInput = z.infer<typeof convertLegacyDepartmentSchema>;

export const legacyDepartmentParamsSchema = z.object({
  id: z.uuid(),
  responsibilityId: z.uuid(),
});

/** Üye kapsamı değişimi (§7.2). */
export const setMemberScopeSchema = z.object({
  memberScope: z.enum(CONTRACT_MEMBER_SCOPES, { message: "Üye kapsamı seçilmelidir" }),
});

/** Seçili ya da sözleşmeye özel üye ekleme (§7.2, §8). */
export const teamMemberSchema = z.object({
  membershipId: z.uuid("Geçerli bir üye seçilmelidir"),
  /** ⚠ Yalnız sözleşmeye özel üyelikte anlamlı: neden bu kişi bu sözleşmede çalışıyor. */
  reason: z.string().trim().max(280, "Gerekçe en fazla 280 karakter olabilir").optional(),
});

export const teamMemberParamsSchema = z.object({
  id: z.uuid(),
  participationId: z.uuid(),
});

export const teamMemberRemoveParamsSchema = z.object({
  id: z.uuid(),
  participationId: z.uuid(),
  membershipId: z.uuid(),
});

export const scopedMemberParamsSchema = z.object({
  id: z.uuid(),
  scopedMemberId: z.uuid(),
});
